"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The eight themes, and how one gets applied.                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Metadata only — the id, the label, and whether the palette is dark. The colours live in
 * `styles/tokens.css`, and `__tests__/theme-parity.test.ts` is what stops the two lists from
 * drifting apart.
 *
 * ⚠️ **The picker's preview swatches are deliberately NOT here.** The mockup hand-picks seven
 * per theme — 56 hex values — and they do not match the tokens they stand for: its swatch for
 * light `--primary` is `#6d3fd4` where the token resolves to `#7851d2`. Nothing in C1 draws a
 * picker, so shipping them now would be 56 hand-maintained values with no consumer and a known
 * discrepancy. C6 builds the picker and should DERIVE them from the tokens. See
 * `docs/plan/debt.md`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_THEME = exports.SYSTEM_THEME = exports.THEMES = void 0;
exports.themeById = themeById;
exports.isDarkTheme = isDarkTheme;
exports.applyTheme = applyTheme;
exports.THEMES = [
    { id: 'light', dark: false },
    { id: 'dark', dark: true },
    { id: 'latte', dark: false },
    { id: 'frappe', dark: true },
    { id: 'macchiato', dark: true },
    { id: 'mocha', dark: true },
    { id: 'nord', dark: true },
    { id: 'radix', dark: true },
];
exports.SYSTEM_THEME = 'system';
/**
 * What a person sees before they have chosen anything.
 *
 * ⚠️ **Forecastify Light, and it used to be `system`.** The column defaulted to "follow the
 * device", which meant a new account's theme depended on the machine it was opened on — and the
 * product owner's decision is that the default is the brand's light palette. `system` stays a
 * choice; it is no longer the one nobody made.
 *
 * ⚠️ **Named here so the three places that need it agree**: `0032`'s column default, the
 * blocking paint script in `index.html`, and `theme-boot.tsx`'s fallback. The inline script cannot
 * import this — it runs before any module — and `theme-boot.test.tsx` is what holds the copy to it.
 */
exports.DEFAULT_THEME = 'light';
function themeById(id) {
    return exports.THEMES.find((theme) => theme.id === id) ?? exports.THEMES[0];
}
/** Whether a choice resolves to a dark palette right now — `system` asks the device. */
function isDarkTheme(id, matchMedia = globalThis.matchMedia) {
    if (id === exports.SYSTEM_THEME) {
        return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return themeById(id).dark;
}
/**
 * Apply a theme to the document.
 *
 * ⚠️ **Both `class="dark"` and `data-theme` are set, and both are needed.** The `dark:`
 * variant is defined as `&:is(.dark *)`, so the class is what makes dark-mode utilities
 * apply; `data-theme` is what selects a named palette's token block. A theme that is dark
 * *and* named — mocha, nord, rosepine, gruvbox — needs both, and setting only one produces a
 * palette with light-mode utilities over it, which looks like a rendering fault rather than a
 * missing attribute.
 */
function applyTheme(id, root = document.documentElement) {
    const dark = isDarkTheme(id);
    const resolved = id === exports.SYSTEM_THEME ? (dark ? 'dark' : 'light') : themeById(id).id;
    root.classList.toggle('dark', dark);
    root.setAttribute('data-theme', resolved);
}
//# sourceMappingURL=theme.js.map